import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjects, usePrefetchProject } from '../hooks/useProjects';
import { PROJECT_STATUSES, PROJECT_TYPES } from '../services/projectsAPI';
import { usePermission, SETTINGS_TABS } from '../contexts/PermissionContext';
import ProjectCard from '../components/ProjectCard';

const PAGE_SIZE_OPTIONS = [12, 24, 48];

function ProjectList() {
  const { canAccessSettingsTab } = usePermission();

  // 只有有權限標籤管理權限的使用者才能建立專案
  const canCreateProject = canAccessSettingsTab(SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT);
  const [filters, setFilters] = useState({
    status: '',
    project_type: '',
    year: '',
    search: '',
  });

  const [sort, setSort] = useState('-updated_at');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const { data, isLoading, error } = useProjects({
    ...filters,
    sort,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });

  const prefetchProject = usePrefetchProject();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? '' : value,
    }));
    setPage(1); // 篩選變更時重置到第一頁
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      search: e.target.value,
    }));
    setPage(1); // 搜尋變更時重置到第一頁
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1); // 每頁筆數變更時重置到第一頁
  };

  // 計算分頁資訊
  const totalItems = data?.pagination?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;

  // 產生年份選項（過去5年到未來2年）
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear - 5 + i);

  return (
    <div className="min-h-screen bg-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 頁面標題 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">專案管理</h1>
            <p className="text-gray-600 mt-2">管理所有問卷評估專案</p>
          </div>
          {canCreateProject && (
            <Link
              to="/projects/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              建立新專案
            </Link>
          )}
        </div>

        {/* 篩選和排序 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* 搜尋 */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                搜尋
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={handleSearchChange}
                placeholder="搜尋專案名稱或代碼..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* 狀態篩選 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                狀態
              </label>
              <select
                value={filters.status || 'all'}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">全部狀態</option>
                {PROJECT_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 類型篩選 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                類型
              </label>
              <select
                value={filters.project_type || 'all'}
                onChange={(e) => handleFilterChange('project_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">全部類型</option>
                {PROJECT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 年份篩選 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                年份
              </label>
              <select
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value === 'all' ? '' : parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">全部年份</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 排序 */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">排序:</label>
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1); // 排序變更時重置到第一頁
                }}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="-created_at">最新建立</option>
                <option value="created_at">最早建立</option>
                <option value="-updated_at">最近更新</option>
                <option value="name">名稱 A-Z</option>
                <option value="-name">名稱 Z-A</option>
              </select>
            </div>

            {data && (
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-600">
                  找到 <span className="font-semibold">{totalItems}</span> 個專案
                </p>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">每頁顯示:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 載入狀態 */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
          </div>
        )}

        {/* 錯誤狀態 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-red-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-red-800">載入失敗: {error.message}</p>
            </div>
          </div>
        )}

        {/* 專案列表 */}
        {!isLoading && !error && data?.data && (
          <>
            {data.data.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">沒有專案</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {canCreateProject ? '開始建立您的第一個專案' : '目前沒有可檢視的專案'}
                </p>
                {canCreateProject && (
                  <div className="mt-6">
                    <Link
                      to="/projects/new"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      建立新專案
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(Array.isArray(data.data) ? data.data : []).map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onPrefetch={prefetchProject}
                    />
                  ))}
                </div>

                {/* 分頁控制 */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      onClick={() => setPage(1)}
                      disabled={!hasPrevPage}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      第一頁
                    </button>
                    <button
                      onClick={() => setPage((p) => p - 1)}
                      disabled={!hasPrevPage}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      上一頁
                    </button>

                    <span className="px-4 py-2 text-sm text-gray-700">
                      第 <span className="font-semibold">{page}</span> / {totalPages} 頁
                    </span>

                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={!hasNextPage}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      下一頁
                    </button>
                    <button
                      onClick={() => setPage(totalPages)}
                      disabled={!hasNextPage}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      最後頁
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProjectList;
